import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Users, MailCheck, ShieldCheck, Crown, TrendingUp, Trophy, FileText, MessageSquare, RefreshCw, Euro, BarChart3, Mail, MegaphoneIcon, Activity,
} from "lucide-react";
import { Button } from "@/components/ui/button";

function StatCard({ icon: Icon, label, value, sub, tone = "indigo", testid }) {
  const toneMap = {
    indigo: "bg-indigo-50 text-indigo-600 border-indigo-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    slate: "bg-slate-50 text-slate-700 border-slate-100",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${toneMap[tone]}`}>
          <Icon size={18} />
        </div>
      </div>
      <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-4">{label}</p>
      <p className="font-display text-3xl font-bold text-slate-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function Sparkline({ data }) {
  if (!data?.length) return null;
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="flex items-end gap-1 h-24" data-testid="signups-sparkline">
      {data.map((d) => (
        <div
          key={d.date}
          className="flex-1 bg-gradient-to-t from-indigo-500 to-indigo-300 rounded-sm min-h-[2px] hover:opacity-80 transition-opacity"
          style={{ height: `${(d.count / max) * 100}%` }}
          title={`${d.date} : ${d.count} inscription(s)`}
        />
      ))}
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role]);

  if (authLoading) return null;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-8" data-testid="admin-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-rose-500 font-semibold">Admin · Pilotage</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2 flex items-center gap-3">
            <BarChart3 size={36} /> Stats temps réel
          </h1>
          <p className="text-slate-600 mt-2">
            Vue d'ensemble : utilisateurs, sécurité, monétisation et engagement.
            {stats?.generated_at && (
              <span className="block text-xs text-slate-400 mt-1">
                Dernière mise à jour : {new Date(stats.generated_at).toLocaleString("fr-FR")}
              </span>
            )}
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-refresh-btn">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </header>

      <Link
        to="/admin/broadcast"
        className="block rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-fuchsia-50 p-5 hover:shadow-md transition-shadow"
        data-testid="admin-broadcast-cta"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-500 text-white flex items-center justify-center shrink-0">
            <MegaphoneIcon size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-lg text-slate-900">Envoyer une notification broadcast →</p>
            <p className="text-sm text-slate-600">Communique avec ta communauté (segments, catégories, priorités, tracking ouvertures/clics).</p>
          </div>
        </div>
      </Link>

      <Link
        to="/admin/observability"
        className="block rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-5 hover:shadow-md transition-shadow"
        data-testid="admin-observability-cta"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
            <Activity size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-lg text-slate-900">Observability — Perfs, KPIs, erreurs →</p>
            <p className="text-sm text-slate-600">Latences p95/p99, taux d'erreur, utilisateurs actifs, taux Premium, incidents antivirus.</p>
          </div>
        </div>
      </Link>

      {loading && !stats && <p className="text-slate-500">Chargement…</p>}

      {stats && (
        <>
          {/* Users block */}
          <section>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Utilisateurs</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                label="Total comptes"
                value={stats.users.total}
                sub={`+${stats.users.new_today} aujourd'hui · +${stats.users.new_7d} sur 7j`}
                tone="indigo"
                testid="stat-users-total"
              />
              <StatCard
                icon={TrendingUp}
                label="Nouveaux (30 jours)"
                value={stats.users.new_30d}
                tone="indigo"
                testid="stat-users-30d"
              />
              <StatCard
                icon={MailCheck}
                label="Email vérifiés"
                value={`${stats.users.email_verified_rate}%`}
                sub={`${stats.users.email_verified} / ${stats.users.total}`}
                tone="emerald"
                testid="stat-email-verified"
              />
              <StatCard
                icon={ShieldCheck}
                label="2FA activée"
                value={`${stats.users.two_fa_rate}%`}
                sub={`${stats.users.two_fa_enabled} comptes · ${stats.users.two_fa_bonus_claimed} bonus réclamés`}
                tone="amber"
                testid="stat-2fa"
              />
            </div>
          </section>

          {/* Premium block */}
          <section>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Monétisation</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Crown}
                label="Premium actifs"
                value={stats.premium.active}
                sub={`Taux de conversion ${stats.premium.conversion_rate}%`}
                tone="amber"
                testid="stat-premium-active"
              />
              <StatCard
                icon={Euro}
                label="MRR estimé"
                value={`${stats.premium.mrr_eur.toFixed(2)} €`}
                sub="Abonnements actifs × 2,99 €"
                tone="emerald"
                testid="stat-mrr"
              />
              <StatCard
                icon={Crown}
                label="Stripe"
                value={stats.premium.stripe_subs}
                sub="abonnements actifs"
                tone="slate"
                testid="stat-stripe-subs"
              />
              <StatCard
                icon={Crown}
                label="PayPal"
                value={stats.premium.paypal_subs}
                sub="abonnements actifs"
                tone="slate"
                testid="stat-paypal-subs"
              />
            </div>
          </section>

          {/* Engagement + Sparkline */}
          <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2" data-testid="signups-chart-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Inscriptions (30 jours)</p>
                  <p className="font-display text-2xl font-bold text-slate-900 mt-1">{stats.users.new_30d} nouveaux</p>
                </div>
                <TrendingUp className="text-indigo-500" size={22} />
              </div>
              <Sparkline data={stats.signups_30d} />
              <div className="flex justify-between text-[10px] text-slate-400 mt-2">
                <span>{stats.signups_30d?.[0]?.date}</span>
                <span>{stats.signups_30d?.[stats.signups_30d.length - 1]?.date}</span>
              </div>
            </div>
            <div className="space-y-4">
              <StatCard
                icon={MessageSquare}
                label="Posts communauté"
                value={stats.engagement.community_posts}
                tone="rose"
                testid="stat-community"
              />
              <StatCard
                icon={FileText}
                label="Documents uploadés"
                value={stats.engagement.documents_uploaded}
                tone="rose"
                testid="stat-documents"
              />
              <StatCard
                icon={Mail}
                label="Newsletter"
                value={stats.engagement.newsletter_subscribers ?? 0}
                sub="abonnés actifs"
                tone="indigo"
                testid="stat-newsletter"
              />
            </div>
          </section>

          {/* Top referrers */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="top-referrers-card">
            <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2 mb-4">
              <Trophy size={20} className="text-amber-500" /> Top 5 parrains
            </h2>
            {stats.top_referrers.length === 0 ? (
              <p className="text-sm text-slate-500">Aucun parrain pour le moment.</p>
            ) : (
              <div className="divide-y divide-slate-100" data-testid="top-referrers-list">
                {stats.top_referrers.map((r, i) => (
                  <div key={r.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-slate-200 text-slate-700" : i === 2 ? "bg-orange-100 text-orange-700" : "bg-slate-50 text-slate-500"
                      }`}>
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{r.name}</p>
                        <p className="text-xs text-slate-500 truncate">{r.email} · code <code className="font-mono">{r.referral_code}</code></p>
                      </div>
                    </div>
                    <span className="shrink-0 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-semibold">
                      {r.count} filleul{r.count > 1 ? "s" : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
